<script setup lang="ts">
import { save, close } from "ionicons/icons";
import {
  IonLabel,
  IonItem,
  IonList,
  IonGrid,
  IonButton,
  IonLoading,
  IonIcon,
  IonTextarea,
  IonRow,
  IonCol,
  IonInput,
  IonFooter,
  IonNote,
  useIonRouter,
  IonAlert,
} from "@ionic/vue";
import { useContextStore } from "@/stores/context";
import { onMounted, reactive } from "vue";
import { editEvent, deleteEvent } from "@/api/events";
import { useToasters } from "@/composition/toasters";
import { useUserStore } from "@/stores/user";
import { handleError } from "@/views";
import { useWindowWidth } from "@/composition/window";
import { event as gtag_event } from "vue-gtag";

const { isMobile } = useWindowWidth();
const toasters = useToasters();
const contextStore = useContextStore();

const emit = defineEmits(["cancel"]);
const router = useIonRouter();
const userStore = useUserStore();

const event = reactive({
  name: "",
  slug: "",
  location: "",
  description: "",
  capacity: 100,
});

const state = reactive({
  loading: true,
  errors: {
    name: [],
    slug: [],
    capacity: [],
    start_date: [],
    end_date: [],
    timezone: [],
  },
  alertDeleteEvent: false,
});

const loadLocalEvent = () => {
  event.name = contextStore.event.name;
  event.description = contextStore.event.description || "";
  event.slug = contextStore.event.slug;
  event.location = contextStore.event.location;
  event.capacity = contextStore.event.capacity;
};

const submit = () => {
  state.loading = true;
  editEvent({ id: contextStore.event.id, ...event })
    .then(() => {
      state.loading = false;
      toasters.addSuccess("Event info saved successfuly");

      if (isMobile) {
        cancel();
      }
    })
    .catch((err) => {
      state.loading = false;
      if (err.response.data && err.response.data[0]) {
        toasters.addError(err.response.data[0]);
      } else {
        handleError(err);
      }
    });
};

const cancel = () => {
  emit("cancel", null);
};

onMounted(() => {
  userStore.getUserPermissions();
  loadLocalEvent();
  state.loading = false;
});

const deleteButtons = [
  {
    text: "Cancel",
    role: "cancel",
    cssClass: "alert-button-cancel",
    handler: () => {
      state.alertDeleteEvent = false;
    },
  },
  {
    text: " OK ",
    role: "confirm",
    cssClass: "alert-button-confirm",
    handler: () => {
      state.alertDeleteEvent = false;
      deleteEventById(contextStore.event.id);
    },
  },
];

const deleteEventById = (event_id: number) => {
  state.loading = true;
  deleteEvent(event_id)
    .then(() => {
      state.loading = false;
      router.push("/home");
      toasters.addSuccess("Event deleted successfully");
      userStore.fetchMe();

      gtag_event("event_deleted", {
        category: "check-in app",
        label: "Event deleted",
      });
    })
    .catch((err) => {
      state.loading = false;
      if (err.response.data && err.response.data[0]) {
        toasters.addError(err.response.data[0]);
      } else {
        handleError(err);
      }
    });
};
</script>
<template>
  <div>
    <ion-item>
      <ion-label>
        <div
          class="flex-desktop"
          style="justify-content: space-between; align-items: center"
        >
          <div>
            <h1>Basic information</h1>
            <p>Be descriptive and name your event.</p>
          </div>
          <div v-if="!isMobile && userStore.userIsAdmin">
            <ion-button
              expand="block"
              color="danger"
              class="ion-margin"
              @click="state.alertDeleteEvent = true"
            >
              DELETE EVENT
            </ion-button>
          </div>
        </div>
      </ion-label>
    </ion-item>
    <ion-list>
      <ion-item>
        <ion-label
          position="stacked"
          :class="{ 'ion-invalid': state.errors.name.length > 0 }"
          >Name</ion-label
        >
        <ion-input v-model="event.name" placeholder="My event"></ion-input>
        <ion-note
          slot="error"
          v-for="(message, i) in state.errors.name"
          :key="`slug-error-${i}`"
        >
          {{ message }}
        </ion-note>
      </ion-item>
      <ion-item>
        <ion-label position="stacked">Slug</ion-label>
        <ion-input v-model="event.slug" placeholder="my-event"></ion-input>
      </ion-item>
      <ion-item :class="{ 'ion-invalid': state.errors.slug.length > 0 }">
        <ion-label position="stacked">Description</ion-label>
        <ion-textarea
          v-model="event.description"
          :style="isMobile ? '' : 'min-height: 120px;'"
          placeholder="Be descriptive and detailed!"
        ></ion-textarea>
        <ion-note
          slot="error"
          v-for="(message, i) in state.errors.slug"
          :key="`slug-error-${i}`"
        >
          {{ message }}
        </ion-note>
      </ion-item>
      <ion-item>
        <ion-label position="stacked">Location</ion-label>
        <ion-input placeholder="New York" v-model="event.location"></ion-input>
      </ion-item>
      <ion-item :class="{ 'ion-invalid': state.errors.capacity.length > 0 }">
        <ion-label position="stacked">Capacity</ion-label>
        <ion-input
          v-model="event.capacity"
          type="number"
          placeholder="1000"
        ></ion-input>
        <ion-note
          slot="error"
          v-for="(message, i) in state.errors.capacity"
          :key="`capacity-error-${i}`"
        >
          {{ message }}
        </ion-note>
      </ion-item>
    </ion-list>
    <ion-loading :is-open="state.loading" message="Please wait...">
    </ion-loading>
    <ion-button
      v-if="isMobile && userStore.userIsAdmin"
      expand="block"
      color="primary"
      class="ion-margin"
      fill="clear"
      @click="state.alertDeleteEvent = true"
    >
      DELETE EVENT
    </ion-button>
    <ion-alert
      :isOpen="state.alertDeleteEvent"
      :buttons="deleteButtons"
      header="Are you sure?"
      message="This will permanently delete your event."
    ></ion-alert>
    <ion-button
      v-if="!isMobile"
      style="margin: 20px auto; width: 200px"
      expand="block"
      color="success"
      @click="submit()"
    >
      <ion-icon slot="start" :icon="save"></ion-icon>
      Save
    </ion-button>
    <ion-loading :is-open="state.loading" message="Please wait...">
    </ion-loading>

    <ion-footer v-if="isMobile">
      <div class="container">
        <section>
          <ion-grid>
            <ion-row>
              <ion-col>
                <ion-button expand="block" color="light" @click="cancel()">
                  <ion-icon slot="start" :icon="close"></ion-icon>
                  Cancel
                </ion-button>
              </ion-col>
              <ion-col>
                <ion-button expand="block" color="success" @click="submit()">
                  <ion-icon slot="start" :icon="save"></ion-icon>
                  Save
                </ion-button>
              </ion-col>
            </ion-row>
          </ion-grid>
        </section>
      </div>
    </ion-footer>
  </div>
</template>
